/**
 * Strategy questionnaire overflow menu — exit assessment.
 *
 * Belongs here: bottom sheet actions from the questionnaire menu.
 * Does not belong here: navigation implementation (passed via callbacks).
 * Event ids default to the strategy questionnaire. Callers can pass their own.
 */
import { useEffect } from 'react'
import BottomSheetContent from '../../../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { SecondaryButton } from '../../../../../components/execution/SecondaryButton'
import { EventNames } from '../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import { ExpertReviewBottomSheet, ExpertReviewEventIds } from '../../analytics/expertReviewEvents'

interface StrategyQuestionnaireMenuSheetProps {
  analyticsScreen: string
  onDismiss: () => void
  onExitAssessment: () => void
  sheetName?: string
  exitEventId?: string
  goBackEventId?: string
}

export function StrategyQuestionnaireMenuSheet({
  analyticsScreen,
  onDismiss,
  onExitAssessment,
  sheetName = ExpertReviewBottomSheet.StrategyMenu,
  exitEventId = ExpertReviewEventIds.MenuExit,
  goBackEventId = ExpertReviewEventIds.MenuGoBack
}: StrategyQuestionnaireMenuSheetProps) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: analyticsScreen,
        eventId: sheetName,
        bottomSheetName: sheetName
      }
    })
  }, [analyticsScreen, sheetName])

  const handleExit = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: exitEventId,
        buttonLabel: 'Exit assessment'
      }
    })
    onDismiss()
    // Dismiss sheet first so exit navigation does not flash the sheet
    onExitAssessment()
  }

  const handleGoBack = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: goBackEventId,
        buttonLabel: 'Go back'
      }
    })
    onDismiss()
  }

  const ctas = (
    <div className='flex flex-col gap-3 px-5 pb-2'>
      <SecondaryButton
        testLocator='strategy-questionnaire-menu-exit'
        label='Exit assessment'
        onClick={handleExit}
        secondaryButtonClassName='!h-14 !rounded-xl'
      />
      <PrimaryButton
        testLocator='strategy-questionnaire-menu-go-back'
        label='Go back'
        onClick={handleGoBack}
        primaryButtonClassName='!h-14 !rounded-xl'
      />
    </div>
  )

  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      bottomSheetHeadClassName='!py-0'
      bottomSheetBodyClassName='!px-0'
      bottomSheetCtas={ctas}
      bottomSheetCtaClassName='!pt-1 !px-0'
      bottomSheetCtaWrapperClassName='!px-0'
    >
      <div />
    </BottomSheetContent>
  )
}
